import { useParams, Link, Navigate } from 'react-router-dom';
import { articles, games, categories } from '@/mocks/gameData';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  if (!slug) {
    return <Navigate to="/" replace />;
  }

  const article = articles.find((a) => a.slug === slug);
  const game = games.find((g) => g.slug === slug);

  // Combine: article takes priority, fallback to game data
  const title = article?.title || game?.title || '';
  const category = article?.category || game?.category || '';
  const categoryName = categories.find((c) => c.slug === category)?.name || category;
  const author = article?.author || game?.developer || '';
  const date = article?.date || game?.releaseDate || '';
  const tags = article?.tags || game?.tags || [];
  const imageUrl = article?.imageUrl || game?.imageUrl || '';
  const readTime = article?.readTime || '';
  const excerpt = article?.excerpt || game?.description || '';

  if (!title) {
    return <Navigate to="/" replace />;
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== slug && a.category === category)
    .slice(0, 3);

  const relatedGames = games
    .filter((g) => g.slug !== slug && g.category === category)
    .slice(0, 3);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: title,
            description: excerpt.slice(0, 160),
            image: imageUrl,
            author: { '@type': 'Person', name: author },
            datePublished: date,
            publisher: {
              '@type': 'Organization',
              name: '黄油游戏大全',
              url: 'https://hdyxdaquan.com',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://hdyxdaquan.com/article/${slug}`,
            },
          }),
        }}
      />

      <article className="pt-20 pb-10 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName, href: `/category/${category}` },
              { label: title },
            ]}
          />

          <div className="max-w-4xl mx-auto mt-6">
            {/* Header */}
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${category}`}
                  className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full hover:bg-accent-200 transition-colors whitespace-nowrap"
                >
                  {categoryName}
                </Link>
                {readTime && (
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {readTime}
                  </span>
                )}
                <span className="text-xs text-foreground-400">{date}</span>
              </div>

              <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 mb-4 leading-tight">{title}</h1>

              <div className="flex items-center gap-3 text-sm text-foreground-500">
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-500 text-xs"></i>
                </div>
                <span>{author}</span>
                <span className="text-foreground-300">·</span>
                <span>黄油游戏大全</span>
              </div>
            </header>

            {/* Featured Image */}
            {imageUrl && (
              <div className="mb-10 rounded-xl overflow-hidden">
                <img src={imageUrl} alt={title} className="w-full object-cover max-h-[400px]" />
              </div>
            )}

            {/* Content */}
            {article ? (
              <div className="prose-custom">
                {article.content.map((paragraph, i) => (
                  <p key={i} className="text-foreground-700 leading-relaxed text-base mb-6">
                    {paragraph}
                  </p>
                ))}
              </div>
            ) : game ? (
              <div className="prose-custom">
                <div className="mb-8">
                  <h2 className="text-xl font-heading text-foreground-900 mb-4">游戏简介</h2>
                  <p className="text-foreground-700 leading-relaxed text-base mb-6">{game.description}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8 p-6 bg-background-100 rounded-xl">
                  <div>
                    <h3 className="text-sm font-medium text-foreground-500 mb-1">开发商</h3>
                    <p className="text-foreground-800">{game.developer}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-500 mb-1">发行日期</h3>
                    <p className="text-foreground-800">{game.releaseDate}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-500 mb-1">平台</h3>
                    <p className="text-foreground-800">{game.platform.join(' / ')}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground-500 mb-1">评分</h3>
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-amber-400"></i>
                      <span className="text-foreground-800 font-medium">{game.rating}</span>
                    </div>
                  </div>
                </div>

                <h2 className="text-xl font-heading text-foreground-900 mb-4">游戏特色</h2>
                <div className="flex flex-wrap gap-2 mb-8">
                  {game.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1.5 bg-secondary-100 text-secondary-700 text-sm rounded-full whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Tags */}
            <div className="border-t border-background-200 pt-6 mt-10">
              <div className="flex items-center flex-wrap gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                {tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/${category}`}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full hover:bg-primary-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>

            {/* Related Content */}
            <div className="border-t border-background-200 pt-10 mt-10">
              {relatedArticles.length > 0 && (
                <div className="mb-10">
                  <h2 className="text-lg md:text-xl font-heading text-foreground-900 mb-6">相关攻略</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra.slug}
                        to={`/article/${ra.slug}`}
                        className="group bg-background-100 rounded-xl p-4 hover:bg-primary-50 transition-colors cursor-pointer"
                      >
                        <h3 className="text-sm font-medium text-foreground-800 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">{ra.title}</h3>
                        <p className="text-xs text-foreground-400 line-clamp-2 mb-3">{ra.excerpt}</p>
                        <span className="text-xs text-primary-500">{ra.readTime}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {relatedGames.length > 0 && (
                <div>
                  <h2 className="text-lg md:text-xl font-heading text-foreground-900 mb-6">相关游戏</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {relatedGames.map((rg) => (
                      <Link
                        key={rg.slug}
                        to={`/article/${rg.slug}`}
                        className="group bg-background-100 rounded-xl overflow-hidden hover:border-primary-200 border border-transparent transition-all cursor-pointer"
                      >
                        <div className="h-32 overflow-hidden">
                          <img src={rg.imageUrl} alt={rg.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                        <div className="p-3">
                          <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors">{rg.title}</h3>
                          <div className="flex items-center gap-1 mt-1">
                            <i className="ri-star-fill text-amber-400 text-xs"></i>
                            <span className="text-xs text-foreground-500">{rg.rating}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </article>
    </>
  );
}